import React, { useState, useEffect, useCallback } from "react";
import { 
  ArrowLeft, Bell, CheckSquare, Users, Plus, Clock, Calendar, 
  User, X, Check, AlertCircle, ChevronDown, ChevronRight,
  Trash2, Edit2, Flag, Mail, Folder, MessageSquare, Sparkles, Stamp
} from "lucide-react";
import EmailManager from "./components/EmailManager";
import GedUserView from "./components/GedUserView";
import TasksPanel from "./components/TasksPanel";
import RappelDetailModal from "./components/calendrier/RappelDetailModal";
import VisaIAPanel from "./components/VisaIAPanel";
import getApiUrl from './utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Composant pour afficher la priorité avec couleur
const PriorityBadge = ({ priority }) => {
  const colors = {
    haute: "bg-red-500/20 text-red-400 border-red-500/30",
    moyenne: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    basse: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
  };
  const labels = { haute: "Haute", moyenne: "Moyenne", basse: "Basse" };
  
  return (
    <span className={`px-2 py-0.5 rounded text-xs border ${colors[priority] || colors.moyenne}`}>
      {labels[priority] || "Moyenne"}
    </span>
  );
};

export default function MonEspace({ onBack, authUser, defaultTab }) {
  const [activeTab, setActiveTab] = useState(typeof defaultTab === 'string' ? defaultTab : "rappels"); // rappels, todos, client_tasks, emails, ged
  const [loading, setLoading] = useState(true);
  
  // Données
  const [rappels, setRappels] = useState([]);
  const [todos, setTodos] = useState([]);
  const [clientTasks, setClientTasks] = useState([]);
  
  // Modals
  const [showAddTodoModal, setShowAddTodoModal] = useState(false);
  const [showAddClientTaskModal, setShowAddClientTaskModal] = useState(false);
  
  // Formulaires
  const [todoForm, setTodoForm] = useState({ titre: "", description: "", priority: "moyenne", deadline: "" });
  const [clientTaskForm, setClientTaskForm] = useState({ titre: "", client_name: "", client_id: "", priority: "moyenne", deadline: "" });
  
  // Stats
  const [stats, setStats] = useState({ rappelsToday: 0, todosPending: 0, clientTasksPending: 0 });
  const [newCounts, setNewCounts] = useState({});
  const [visaPendingCount, setVisaPendingCount] = useState(0);
  
  // Séparer rappels clients vs rappels emails
  const clientRappels = rappels.filter(r => r.source !== "email");
  const emailRappels = rappels.filter(r => r.source === "email");
  
  // État pour l'édition de rappels
  const [selectedRappel, setSelectedRappel] = useState(null);
  const [loadingRappelDetail, setLoadingRappelDetail] = useState(false);

  // Accepter/Ignorer/Terminer un rappel email
  const handleEmailReminderAction = async (reminderId, action) => {
    if (!reminderId) return;
    try {
      await authFetch(`${API}/api/email/reminder/${encodeURIComponent(reminderId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      loadData();
    } catch (e) {
      console.error("Erreur action rappel:", e);
    }
  };



  // Marquer un onglet comme vu
  const markTabSeen = useCallback(async (tab) => {
    if (!authUser?.email) return;
    try {
      await authFetch(`${API}/api/user/espace/tab-seen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: authUser.email, tab })
      });
      setNewCounts(prev => ({ ...prev, [tab]: 0 }));
    } catch (e) {
      console.error("Erreur mark tab seen:", e);
    }
  }, [authUser?.email]);

  // Changer d'onglet et marquer comme vu
  const handleTabClick = useCallback((tab) => {
    setActiveTab(tab);
    markTabSeen(tab);
  }, [markTabSeen]);

  // Charger les données de l'utilisateur
  const loadData = useCallback(async () => {
    if (!authUser?.email) return;
    
    setLoading(true);
    try {
      const [resp, visaResp] = await Promise.all([
        authFetch(`${API}/api/user/espace?user_email=${encodeURIComponent(authUser.email)}`),
        authFetch(`${API}/api/visa-ia/count`)
      ]);
      if (resp.ok) {
        const data = await resp.json();
        setRappels(data.rappels || []);
        setTodos(data.todos || []);
        setClientTasks(data.client_tasks || []);
        setStats(data.stats || { rappelsToday: 0, todosPending: 0, clientTasksPending: 0 });
        setNewCounts(data.new_counts || {});
      }
      if (visaResp.ok) {
        const vd = await visaResp.json();
        setVisaPendingCount(vd.count || 0);
      }
    } catch (e) {
      console.error("Erreur chargement espace:", e);
    }
    setLoading(false);
  }, [authUser?.email]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Marquer l'onglet initial comme vu au premier chargement
  useEffect(() => {
    if (activeTab && authUser?.email) {
      markTabSeen(activeTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.email]);

  // Marquer un rappel comme fait
  const toggleRappelDone = async (rappelId) => {
    try {
      const resp = await authFetch(`${API}/api/user/espace/rappel/${rappelId}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: authUser.email })
      });
      if (resp.ok) loadData();
    } catch (e) {
      console.error("Erreur toggle rappel:", e);
    }
  };

  // Ouvrir le détail riche d'un rappel
  const openRappelDetail = async (rappel) => {
    // Pour les leads, on récupère les données complètes
    if (rappel.source === "lead" && rappel.id?.startsWith("lead_")) {
      const leadId = rappel.id.replace("lead_", "");
      setLoadingRappelDetail(true);
      try {
        const resp = await authFetch(`${API}/api/webhook/leads/${leadId}`);
        if (resp.ok) {
          const leadData = await resp.json();
          setSelectedRappel(leadData);
        }
      } catch (e) {
        console.error("Erreur chargement lead:", e);
      }
      setLoadingRappelDetail(false);
      return;
    }
    
    // Pour les qualifications (pipeline_clients), mapper les données
    if (rappel.source === "qualification" && rappel.id?.startsWith("pc_")) {
      setSelectedRappel({
        lead_id: rappel.id,
        nom: (rappel.client_name || "").split(" ")[0] || "",
        prenom: (rappel.client_name || "").split(" ").slice(1).join(" ") || "",
        telephone: rappel.telephone || "",
        date_rappel: rappel.date,
        heure_rappel: rappel.heure,
        notes: rappel.motif,
        departement: rappel.departement,
        source: "qualification",
        _source_type: "qualification"
      });
      return;
    }
    
    // Pour les rappels sheet (standard), mapper les données
    setSelectedRappel({
      lead_id: rappel.id,
      nom: (rappel.client_name || "").split(" ")[0] || "",
      prenom: (rappel.client_name || "").split(" ").slice(1).join(" ") || "",
      telephone: rappel.telephone || "",
      date_rappel: rappel.date,
      heure_rappel: rappel.heure,
      notes: rappel.motif,
      row_number: rappel.row_number,
      source: rappel.source || "fiche_client",
      _source_type: "sheet"
    });
  };

  // Supprimer un rappel
  const deleteRappel = async (rappel) => {
    if (!window.confirm("Supprimer ce rappel ?")) return;
    const rappelId = rappel.source === "email" ? rappel.reminder_id : rappel.id;
    try {
      const resp = await authFetch(`${API}/api/user/espace/rappel/${encodeURIComponent(rappelId)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: authUser.email })
      });
      if (resp.ok) loadData();
    } catch (e) {
      console.error("Erreur suppression rappel:", e);
    }
  };

  // Ajouter une todo
  const addTodo = async () => {
    if (!todoForm.titre) {
      alert("Le titre est requis");
      return;
    }
    
    try {
      const resp = await authFetch(`${API}/api/user/espace/todo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: authUser.email,
          ...todoForm
        })
      });
      if (resp.ok) {
        setShowAddTodoModal(false);
        setTodoForm({ titre: "", description: "", priority: "moyenne", deadline: "" });
        loadData();
      }
    } catch (e) {
      console.error("Erreur ajout todo:", e);
    }
  };

  // Toggle todo
  const toggleTodo = async (todoId) => {
    try {
      const resp = await authFetch(`${API}/api/user/espace/todo/${todoId}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: authUser.email })
      });
      if (resp.ok) loadData();
    } catch (e) {
      console.error("Erreur toggle todo:", e);
    }
  };

  // Supprimer todo
  const deleteTodo = async (todoId) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    
    try {
      const resp = await authFetch(`${API}/api/user/espace/todo/${todoId}?user_email=${encodeURIComponent(authUser.email)}`, {
        method: 'DELETE'
      });
      if (resp.ok) loadData();
    } catch (e) {
      console.error("Erreur suppression todo:", e);
    }
  };

  // Ajouter une tâche client
  const addClientTask = async () => {
    if (!clientTaskForm.titre || !clientTaskForm.client_name) {
      alert("Le titre et le client sont requis");
      return;
    }
    
    try {
      const resp = await authFetch(`${API}/api/user/espace/client-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: authUser.email,
          ...clientTaskForm
        })
      });
      if (resp.ok) {
        setShowAddClientTaskModal(false);
        setClientTaskForm({ titre: "", client_name: "", client_id: "", priority: "moyenne", deadline: "" });
        loadData();
      }
    } catch (e) {
      console.error("Erreur ajout tâche client:", e);
    }
  };

  // Toggle client task
  const toggleClientTask = async (taskId) => {
    try {
      const resp = await authFetch(`${API}/api/user/espace/client-task/${taskId}/toggle`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: authUser.email })
      });
      if (resp.ok) loadData();
    } catch (e) {
      console.error("Erreur toggle tâche client:", e);
    }
  };

  // Supprimer client task
  const deleteClientTask = async (taskId) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    
    try {
      const resp = await authFetch(`${API}/api/user/espace/client-task/${taskId}?user_email=${encodeURIComponent(authUser.email)}`, {
        method: 'DELETE'
      });
      if (resp.ok) loadData();
    } catch (e) {
      console.error("Erreur suppression tâche client:", e);
    }
  };

  // Formater la date pour affichage
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
    if (date.toDateString() === tomorrow.toDateString()) return "Demain";
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  // Vérifier si une date est passée
  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date(new Date().toDateString());
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="bg-slate-900/80 border-b border-slate-800 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <User className="w-6 h-6 text-blue-400" />
                Mon Espace
              </h1>
              <p className="text-sm text-slate-400">{authUser?.name || authUser?.email}</p>
            </div>
          </div>
          
          {/* Stats rapides */}
          <div className="flex items-center gap-4">
            <div className="text-center px-4 py-2 bg-orange-500/10 rounded-lg border border-orange-500/20">
              <p className="text-2xl font-bold text-orange-400">{stats.rappelsToday}</p>
              <p className="text-xs text-slate-400">Rappels aujourd'hui</p>
            </div>
            <div className="text-center px-4 py-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
              <p className="text-2xl font-bold text-blue-400">{stats.todosPending}</p>
              <p className="text-xs text-slate-400">Todos en cours</p>
            </div>
          </div>
        </div>
      </header>

      {/* Onglets */}
      <div className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => handleTabClick("rappels")}
            className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === "rappels" 
                ? "bg-orange-500/20 text-orange-400 border border-orange-500/30" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
            data-testid="tab-rappels"
          >
            <Bell className="w-4 h-4" />
            Rappels clients / leads ({clientRappels.filter(r => !r.fait).length})
            {newCounts.rappels > 0 && activeTab !== "rappels" && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                {newCounts.rappels}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabClick("rappels_emails")}
            className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === "rappels_emails" 
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
            data-testid="tab-rappels-emails"
          >
            <Mail className="w-4 h-4" />
            Rappels emails ({emailRappels.filter(r => !r.fait && r.status !== "ignored").length})
            {emailRappels.filter(r => r.status === "suggested").length > 0 && activeTab !== "rappels_emails" && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold bg-purple-500 text-white rounded-full animate-pulse">
                {emailRappels.filter(r => r.status === "suggested").length}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabClick("taches_internes")}
            className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === "taches_internes" 
                ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
            data-testid="tab-taches-internes"
          >
            <MessageSquare className="w-4 h-4" />
            Communication interne ({stats.internalTasksPending || 0})
            {newCounts.taches_internes > 0 && activeTab !== "taches_internes" && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                {newCounts.taches_internes}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabClick("todos")}
            className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === "todos" 
                ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
            data-testid="tab-todos"
          >
            <CheckSquare className="w-4 h-4" />
            Ma Todo ({todos.filter(t => !t.done).length})
            {newCounts.todos > 0 && activeTab !== "todos" && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                {newCounts.todos}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabClick("emails")}
            className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === "emails" 
                ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
            data-testid="tab-emails"
          >
            <Mail className="w-4 h-4" />
            Emails
            {newCounts.emails > 0 && activeTab !== "emails" && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                {newCounts.emails}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabClick("ged")}
            className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === "ged" 
                ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
            data-testid="tab-ged"
          >
            <Folder className="w-4 h-4" />
            Documents (GED)
            {newCounts.ged > 0 && activeTab !== "ged" && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                {newCounts.ged}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabClick("visa_ia")}
            className={`relative px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeTab === "visa_ia" 
                ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
            }`}
            data-testid="tab-visa-ia"
          >
            <Stamp className="w-4 h-4" />
            Visa IA
            {visaPendingCount > 0 && activeTab !== "visa_ia" && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold bg-purple-500 text-white rounded-full animate-pulse">
                {visaPendingCount}
              </span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <>
            {/* Onglet Rappels Clients / Leads */}
            {activeTab === "rappels" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Bell className="w-5 h-5 text-orange-400" />
                    Mes rappels clients / leads
                  </h2>
                  <p className="text-sm text-slate-400">
                    Les rappels sont créés depuis les fiches clients
                  </p>
                </div>
                
                {clientRappels.length === 0 ? (
                  <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
                    <Bell className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">Aucun rappel client programmé</p>
                    <p className="text-sm text-slate-500 mt-1">Ajoutez des rappels depuis les fiches clients</p>
                  </div>
                ) : (
                  clientRappels.map((rappel, idx) => (
                    <div 
                      key={rappel.id || idx}
                      onClick={() => openRappelDetail(rappel)}
                      className={`p-4 rounded-xl border transition-colors cursor-pointer hover:ring-1 hover:ring-orange-400/40 ${
                        rappel.fait 
                          ? "bg-slate-900/30 border-slate-800 opacity-60" 
                          : isOverdue(rappel.date)
                            ? "bg-red-500/10 border-red-500/30"
                            : "bg-orange-500/10 border-orange-500/20"
                      }`}
                      data-testid={`rappel-client-item-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleRappelDone(rappel.id); }}
                            className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                              rappel.fait 
                                ? "bg-emerald-500 border-emerald-500" 
                                : "border-orange-400 hover:bg-orange-500/20"
                            }`}
                            data-testid={`toggle-client-rappel-${idx}`}
                            title={rappel.fait ? "Marquer non fait" : "Marquer comme fait"}
                          >
                            {rappel.fait && <Check className="w-4 h-4 text-white" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`font-medium ${rappel.fait ? "line-through text-slate-500" : ""}`}>
                                {rappel.motif}
                              </p>
                              {rappel.source === "qualification" && (
                                <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs flex items-center gap-1">
                                  <Users className="w-3 h-3" /> Qualification
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-400">
                              Client: <span className="text-white">{rappel.client_name || "N/A"}</span>
                              {rappel.telephone && <span className="ml-2 text-slate-500">{rappel.telephone}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1 flex-shrink-0 ml-3">
                          <p className={`font-medium ${isOverdue(rappel.date) && !rappel.fait ? "text-red-400" : "text-orange-400"}`}>
                            {formatDate(rappel.date)} {rappel.heure && `à ${rappel.heure}`}
                          </p>
                          {isOverdue(rappel.date) && !rappel.fait && (
                            <p className="text-xs text-red-400">En retard</p>
                          )}
                          <div className="flex gap-1 mt-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteRappel(rappel); }}
                              className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                              data-testid={`delete-client-rappel-${idx}`}
                              title="Supprimer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Onglet Rappels Emails */}
            {activeTab === "rappels_emails" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Mail className="w-5 h-5 text-cyan-400" />
                    Rappels emails
                    {emailRappels.filter(r => r.status === "suggested").length > 0 && (
                      <span className="text-sm font-normal text-purple-400 ml-2">
                        {emailRappels.filter(r => r.status === "suggested").length} suggestion(s) Christelle
                      </span>
                    )}
                  </h2>
                  <p className="text-sm text-slate-400">
                    Rappels créés depuis vos emails
                  </p>
                </div>
                
                {emailRappels.length === 0 ? (
                  <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
                    <Mail className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">Aucun rappel email</p>
                    <p className="text-sm text-slate-500 mt-1">Classez vos emails avec Christelle pour recevoir des suggestions</p>
                  </div>
                ) : (
                  emailRappels.map((rappel, idx) => (
                    <div 
                      key={rappel.reminder_id || idx}
                      className={`p-4 rounded-xl border transition-colors ${
                        rappel.fait 
                          ? "bg-slate-900/30 border-slate-800 opacity-60" 
                          : rappel.status === "suggested"
                            ? "bg-purple-500/10 border-purple-500/30"
                            : isOverdue(rappel.date)
                              ? "bg-red-500/10 border-red-500/30"
                              : "bg-cyan-500/10 border-cyan-500/20"
                      }`}
                      data-testid={`rappel-email-item-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {rappel.status !== "suggested" && (
                            <button
                              onClick={() => handleEmailReminderAction(rappel.reminder_id, "done")}
                              className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                                rappel.fait 
                                  ? "bg-emerald-500 border-emerald-500" 
                                  : "border-cyan-400 hover:bg-cyan-500/20"
                              }`}
                              data-testid={`toggle-email-rappel-${idx}`}
                              title={rappel.fait ? "Marquer non fait" : "Marquer comme fait"}
                            >
                              {rappel.fait && <Check className="w-4 h-4 text-white" />}
                            </button>
                          )}
                          {rappel.status === "suggested" && (
                            <div className="w-6 h-6 rounded-full bg-purple-500/20 border-2 border-purple-400 flex-shrink-0 flex items-center justify-center">
                              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`font-medium ${rappel.fait ? "line-through text-slate-500" : ""}`}>
                                {rappel.motif}
                              </p>
                              {rappel.suggested_by_ai && rappel.status === "suggested" && (
                                <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded text-xs flex items-center gap-1">
                                  <Sparkles className="w-3 h-3" /> Suggéré par Christelle
                                </span>
                              )}
                            </div>
                            {rappel.client_name && (
                              <p className="text-sm text-slate-400">
                                Client: <span className="text-white">{rappel.client_name}</span>
                              </p>
                            )}
                            {rappel.email_subject && (
                              <p className="text-xs text-slate-500 mt-0.5 truncate max-w-md">
                                Re: {rappel.email_subject}
                              </p>
                            )}
                            {rappel.email_from && (
                              <p className="text-xs text-slate-600 mt-0.5">
                                De: {rappel.email_from}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1 flex-shrink-0 ml-3">
                          <p className={`font-medium ${isOverdue(rappel.date) && !rappel.fait ? "text-red-400" : "text-cyan-400"}`}>
                            {formatDate(rappel.date)} {rappel.heure && `à ${rappel.heure}`}
                          </p>
                          {isOverdue(rappel.date) && !rappel.fait && rappel.status !== "suggested" && (
                            <p className="text-xs text-red-400">En retard</p>
                          )}
                          {/* Boutons Accepter/Ignorer pour suggestions Christelle */}
                          {rappel.status === "suggested" && (
                            <div className="flex gap-1 mt-1">
                              <button
                                onClick={() => handleEmailReminderAction(rappel.reminder_id, "accept")}
                                className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded text-xs flex items-center gap-1"
                                data-testid={`accept-email-reminder-${idx}`}
                              >
                                <Check className="w-3 h-3" /> Accepter
                              </button>
                              <button
                                onClick={() => handleEmailReminderAction(rappel.reminder_id, "ignore")}
                                className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs flex items-center gap-1"
                                data-testid={`ignore-email-reminder-${idx}`}
                              >
                                <X className="w-3 h-3" /> Ignorer
                              </button>
                            </div>
                          )}
                          {/* Boutons Supprimer pour rappels emails actifs */}
                          {rappel.status !== "suggested" && (
                            <div className="flex gap-1 mt-1">
                              <button
                                onClick={() => deleteRappel(rappel)}
                                className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                data-testid={`delete-email-rappel-${idx}`}
                                title="Supprimer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* RappelDetailModal — popup riche (même que la partie Télépro) */}
            {selectedRappel && (
              <RappelDetailModal
                rappel={selectedRappel}
                onClose={() => { setSelectedRappel(null); loadData(); }}
                onDelete={() => { setSelectedRappel(null); loadData(); }}
                authUser={authUser}
              />
            )}
            {loadingRappelDetail && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full"></div>
              </div>
            )}

            {/* Onglet Todo List */}
            {activeTab === "todos" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <CheckSquare className="w-5 h-5 text-blue-400" />
                    Ma Todo List
                  </h2>
                  <button
                    onClick={() => setShowAddTodoModal(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg flex items-center gap-2"
                    data-testid="add-todo-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Nouvelle tâche
                  </button>
                </div>
                
                {todos.length === 0 ? (
                  <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
                    <CheckSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">Aucune tâche</p>
                    <button
                      onClick={() => setShowAddTodoModal(true)}
                      className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg inline-flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter une tâche
                    </button>
                  </div>
                ) : (
                  todos.map((todo, idx) => (
                    <div 
                      key={todo.id || idx}
                      className={`p-4 rounded-xl border transition-colors ${
                        todo.done 
                          ? "bg-slate-900/30 border-slate-800 opacity-60" 
                          : isOverdue(todo.deadline)
                            ? "bg-red-500/10 border-red-500/30"
                            : "bg-blue-500/10 border-blue-500/20"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleTodo(todo.id)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              todo.done 
                                ? "bg-emerald-500 border-emerald-500" 
                                : "border-blue-400 hover:bg-blue-500/20"
                            }`}
                          >
                            {todo.done && <Check className="w-4 h-4 text-white" />}
                          </button>
                          <div>
                            <p className={`font-medium ${todo.done ? "line-through text-slate-500" : ""}`}>
                              {todo.titre}
                            </p>
                            {todo.description && (
                              <p className="text-sm text-slate-400">{todo.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <PriorityBadge priority={todo.priority} />
                          {todo.deadline && (
                            <span className={`text-sm ${isOverdue(todo.deadline) && !todo.done ? "text-red-400" : "text-slate-400"}`}>
                              {formatDate(todo.deadline)}
                            </span>
                          )}
                          <button
                            onClick={() => deleteTodo(todo.id)}
                            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Onglet Tâches Clients */}
            {activeTab === "client_tasks" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-400" />
                    Tâches Clients
                  </h2>
                  <button
                    onClick={() => setShowAddClientTaskModal(true)}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg flex items-center gap-2"
                    data-testid="add-client-task-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Nouvelle tâche
                  </button>
                </div>
                
                {clientTasks.length === 0 ? (
                  <div className="text-center py-12 bg-slate-900/50 rounded-xl border border-slate-800">
                    <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">Aucune tâche client</p>
                    <button
                      onClick={() => setShowAddClientTaskModal(true)}
                      className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg inline-flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter une tâche
                    </button>
                  </div>
                ) : (
                  clientTasks.map((task, idx) => (
                    <div 
                      key={task.id || idx}
                      className={`p-4 rounded-xl border transition-colors ${
                        task.done 
                          ? "bg-slate-900/30 border-slate-800 opacity-60" 
                          : isOverdue(task.deadline)
                            ? "bg-red-500/10 border-red-500/30"
                            : "bg-purple-500/10 border-purple-500/20"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => toggleClientTask(task.id)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              task.done 
                                ? "bg-emerald-500 border-emerald-500" 
                                : "border-purple-400 hover:bg-purple-500/20"
                            }`}
                          >
                            {task.done && <Check className="w-4 h-4 text-white" />}
                          </button>
                          <div>
                            <p className={`font-medium ${task.done ? "line-through text-slate-500" : ""}`}>
                              {task.titre}
                            </p>
                            <p className="text-sm text-slate-400">
                              Client: <span className="text-purple-400">{task.client_name}</span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <PriorityBadge priority={task.priority} />
                          {task.deadline && (
                            <span className={`text-sm ${isOverdue(task.deadline) && !task.done ? "text-red-400" : "text-slate-400"}`}>
                              {formatDate(task.deadline)}
                            </span>
                          )}
                          <button
                            onClick={() => deleteClientTask(task.id)}
                            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Onglet Emails */}
            {activeTab === "emails" && (
              <EmailManager authUser={authUser} />
            )}

            {/* Onglet GED */}
            {activeTab === "ged" && (
              <GedUserView authUser={authUser} />
            )}

            {/* Onglet Taches Internes */}
            {activeTab === "taches_internes" && (
              <TasksPanel authUser={authUser} />
            )}

            {/* Onglet Visa IA */}
            {activeTab === "visa_ia" && (
              <VisaIAPanel authUser={authUser} />
            )}
          </>
        )}
      </div>

      {/* Modal Ajouter Todo */}
      {showAddTodoModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-blue-400" />
              Nouvelle tâche
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Titre *</label>
                <input
                  type="text"
                  value={todoForm.titre}
                  onChange={(e) => setTodoForm({...todoForm, titre: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                  placeholder="Ex: Préparer le rapport"
                  data-testid="todo-titre-input"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Description</label>
                <textarea
                  value={todoForm.description}
                  onChange={(e) => setTodoForm({...todoForm, description: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg resize-none"
                  rows={2}
                  placeholder="Détails optionnels..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Priorité</label>
                  <select
                    value={todoForm.priority}
                    onChange={(e) => setTodoForm({...todoForm, priority: e.target.value})}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                    data-testid="todo-priority-select"
                  >
                    <option value="basse">Basse</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="haute">Haute</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Échéance</label>
                  <input
                    type="date"
                    value={todoForm.deadline}
                    onChange={(e) => setTodoForm({...todoForm, deadline: e.target.value})}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                    data-testid="todo-deadline-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddTodoModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={addTodo}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg"
                data-testid="confirm-todo-btn"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter Tâche Client */}
      {showAddClientTaskModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-400" />
              Nouvelle tâche client
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Titre de la tâche *</label>
                <input
                  type="text"
                  value={clientTaskForm.titre}
                  onChange={(e) => setClientTaskForm({...clientTaskForm, titre: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                  placeholder="Ex: Appeler pour confirmation"
                  data-testid="client-task-titre-input"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nom du client *</label>
                <input
                  type="text"
                  value={clientTaskForm.client_name}
                  onChange={(e) => setClientTaskForm({...clientTaskForm, client_name: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                  placeholder="Ex: DUPONT Jean"
                  data-testid="client-task-client-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Priorité</label>
                  <select
                    value={clientTaskForm.priority}
                    onChange={(e) => setClientTaskForm({...clientTaskForm, priority: e.target.value})}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                    data-testid="client-task-priority-select"
                  >
                    <option value="basse">Basse</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="haute">Haute</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Échéance</label>
                  <input
                    type="date"
                    value={clientTaskForm.deadline}
                    onChange={(e) => setClientTaskForm({...clientTaskForm, deadline: e.target.value})}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                    data-testid="client-task-deadline-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddClientTaskModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={addClientTask}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg"
                data-testid="confirm-client-task-btn"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
